import { GEO_GATING_CONFIG, GeoDecision } from "../config/geoGating";

interface GeoSignals {
  country: string | null;
  language: string | null;
  source: "primary" | "fallback" | "none" | "override" | "cache";
}

interface CachedDecision {
  decision: GeoDecision;
  checkedAt: string;
}

// ────────────────────────────────────────────────────────────────────────────
// fetchCountry — IP-basierte Länder-Erkennung mit Fallback + Timeout
// ────────────────────────────────────────────────────────────────────────────

async function fetchWithTimeout(url: string, timeoutMs: number): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

function isValidCountryCode(value: unknown): value is string {
  return typeof value === "string" && /^[A-Za-z]{2}$/.test(value);
}

async function tryPrimary(): Promise<string | null> {
  try {
    const response = await fetchWithTimeout(GEO_GATING_CONFIG.primaryGeoService, GEO_GATING_CONFIG.timeoutMs);
    if (!response.ok) return null;
    const data = (await response.json()) as { country_code?: unknown; country?: unknown };
    const raw = data.country_code ?? data.country;
    return isValidCountryCode(raw) ? raw.toUpperCase() : null;
  } catch {
    return null;
  }
}

async function tryFallback(): Promise<string | null> {
  try {
    const response = await fetchWithTimeout(GEO_GATING_CONFIG.fallbackGeoService, GEO_GATING_CONFIG.timeoutMs);
    if (!response.ok) return null;
    const text = await response.text();
    const match = text.match(/^loc=([A-Za-z]{2})$/m);
    if (!match) return null;
    return isValidCountryCode(match[1]) ? match[1].toUpperCase() : null;
  } catch {
    return null;
  }
}

export async function fetchCountry(): Promise<{ country: string | null; source: "primary" | "fallback" | "none" }> {
  const fromPrimary = await tryPrimary();
  if (fromPrimary) return { country: fromPrimary, source: "primary" };

  const fromFallback = await tryFallback();
  if (fromFallback) return { country: fromFallback, source: "fallback" };

  return { country: null, source: "none" };
}

// ────────────────────────────────────────────────────────────────────────────
// detectLanguage — Browser-Sprache (Präferenz: navigator.languages)
// ────────────────────────────────────────────────────────────────────────────

export function detectLanguage(): string | null {
  if (typeof navigator === "undefined") return null;

  const languages = navigator.languages;
  if (Array.isArray(languages) && languages.length > 0) {
    return languages[0] ?? null;
  }
  return navigator.language ?? null;
}

// ────────────────────────────────────────────────────────────────────────────
// scoreSignals — Reine Funktion, implementiert die Entscheidungstabelle
// ────────────────────────────────────────────────────────────────────────────

function languagesToCheck(language: string | null): string[] {
  if (!language) return [];
  // Beim Scoring schauen wir auf navigator.languages-Array bzw. den Wert
  // den detectLanguage geliefert hat. detectLanguage gibt nur den ersten
  // Eintrag zurück; für die "irgendein de-Match"-Regel müssten wir das
  // ganze Array prüfen. Daher: scoreSignals akzeptiert auch Komma-Listen
  // und navigator.languages-Werte gleichermaßen.
  return [language];
}

function startsWithAnyPrefix(value: string, prefixes: readonly string[]): boolean {
  const lower = value.toLowerCase();
  return prefixes.some((p) => lower.startsWith(p.toLowerCase()));
}

export function scoreSignals(country: string | null, language: string | null): GeoDecision {
  // Fail-open: jede fehlende Information → KVA zeigen.
  if (!country) return "kva";
  if (!language) return "kva";

  const upperCountry = country.toUpperCase();
  const isSensitive = (GEO_GATING_CONFIG.sensitiveCountries as readonly string[]).includes(upperCountry);
  if (!isSensitive) return "kva";

  const langs = languagesToCheck(language);

  // Wenn IRGENDEIN Sprach-Eintrag deutsch ist → KVA (Grenzkunden-Schutz).
  const hasGerman = langs.some((l) => startsWithAnyPrefix(l, GEO_GATING_CONFIG.germanLanguagePrefixes));
  if (hasGerman) return "kva";

  // Sonst: erster Eintrag ist nl oder fr → Info-Seite.
  const first = langs[0];
  if (first && startsWithAnyPrefix(first, GEO_GATING_CONFIG.nlBeLanguagePrefixes)) {
    return "info";
  }

  // Andere Sprache (en, es, …) → KVA (im Zweifel pro Kunde).
  return "kva";
}

// ────────────────────────────────────────────────────────────────────────────
// determineGeoDecision — Hauptfunktion mit Override + Cache + Multi-Signal
// ────────────────────────────────────────────────────────────────────────────

function safeReadSession(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeWriteSession(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // sessionStorage kann im Inkognito-Modus deaktiviert sein — silent fail.
  }
}

function readCache(): CachedDecision | null {
  const raw = safeReadSession(GEO_GATING_CONFIG.sessionStorageKey);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as CachedDecision;
    if (parsed.decision === "kva" || parsed.decision === "info") {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

function writeCache(decision: GeoDecision): void {
  const entry: CachedDecision = { decision, checkedAt: new Date().toISOString() };
  safeWriteSession(GEO_GATING_CONFIG.sessionStorageKey, JSON.stringify(entry));
}

function checkOverrideParam(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get(GEO_GATING_CONFIG.overrideQueryParam) === GEO_GATING_CONFIG.overrideQueryValue;
  } catch {
    return false;
  }
}

function checkPreviewParam(): GeoDecision | null {
  if (typeof window === "undefined") return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const value = params.get(GEO_GATING_CONFIG.previewQueryParam);
    if (value === "info" || value === "kva") return value;
    return null;
  } catch {
    return null;
  }
}

export async function determineGeoDecision(): Promise<{ decision: GeoDecision; signals: GeoSignals }> {
  // 0) Preview-Param für interne Tests (?_geoPreview=info|kva) — überstimmt alles,
  // wird NICHT gecached, damit man frei wechseln kann.
  const preview = checkPreviewParam();
  if (preview) {
    return {
      decision: preview,
      signals: { country: null, language: null, source: "override" },
    };
  }

  // 1) Override-Parameter (?region=de) hat Vorrang über alles außer Preview.
  if (checkOverrideParam()) {
    writeCache("kva");
    return {
      decision: "kva",
      signals: { country: null, language: null, source: "override" },
    };
  }

  // 2) Cache aus sessionStorage (kein Netzwerkcall bei Re-Render/Navigation).
  const cached = readCache();
  if (cached) {
    return {
      decision: cached.decision,
      signals: { country: null, language: null, source: "cache" },
    };
  }

  // 3) Sprache (sofort verfügbar) + Land (parallel via Netzwerk).
  const language = detectLanguage();
  const { country, source } = await fetchCountry();

  // 4) Scoring auf Basis beider Signale.
  const decision = scoreSignals(country, language);

  // 5) Cachen damit Folge-Aufrufe ohne Netzwerk-Roundtrip auskommen.
  writeCache(decision);

  return {
    decision,
    signals: { country, language, source },
  };
}
